import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextareaAutosize, List, ListItem, ListItemText, IconButton, MenuItem, Select } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const SourceManagerComponent = () => {
    const { user } = useUser();
    const [sources, setSources] = useState([]);
    const [currentSource, setCurrentSource] = useState({ id: null, name: '', site_id: '', extra: '{}' });
    const [extraJSON, setExtraJSON] = useState('{}');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [sites, setSites] = useState([]);

    useEffect(() => {
        const fetchSources = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/sources`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSources(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching sources');
            }
        };

        const fetchSites = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/sites`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSites(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching sites');
            }
        };

        if (user.token) {
            fetchSources();
            fetchSites();
        }
    }, [user]);

    const handleOpen = (source) => {
        setIsEditMode(!!source);
        setCurrentSource(source || { id: null, name: '', site_id: '', extra: '{}' });
        setExtraJSON(source ? JSON.stringify(source.extra, null, 2) : '{}');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentSource({ id: null, name: '', site_id: '', extra: '{}' });
        setExtraJSON('{}');
        setError('');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setCurrentSource({ ...currentSource, [name]: value });
    };

    const handleExtraChange = (event) => {
        const { value } = event.target;
        setExtraJSON(value);
    };

    const handleAddOrUpdateSource = async () => {
        try {
            const parsedExtra = JSON.parse(extraJSON);
            const updatedSource = { ...currentSource, extra: parsedExtra };

            if (isEditMode) {
                const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/sources/${currentSource.id}`, updatedSource, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSources(sources.map((source) => (source.id === currentSource.id ? response.data : source)));
            } else {
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/sources`, updatedSource, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSources([...sources, response.data]);
            }
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the source');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/sources/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setSources(sources.filter(source => source.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while deleting the source');
        }
    };

    if (!user.token) {
        return <p>You must be logged in to manage sources.</p>;
    }

    const sourceTemplates = [
        {
            label: 'Phone',
            data: {
                name: 'soh00000',
                extra: { type: "phonebox", phonename: "soh00000", "date_from": "20250414_00-00-00" }
            }
        },
        {
            label: 'Phone with peak2peak filter',
            data: {
                name: 'soh00000',
                extra: {
                    "type": "phonebox",
                    "date_from": "20250414_00-00-00",
                    "phonename": "soh00141",
                    "transforms": [
                        {
                            "type": "rms",
                            "period": 600,
                            "bandpass": [
                                0.1,
                                0.3
                            ],
                            "channels": [
                                "a_x",
                                "a_y",
                                "a_z"
                            ]
                        },
                        {
                            "type": "peak2peak",
                            "period": 60,
                            "bandpass": [
                                0.1,
                                0.3
                            ],
                            "channels": [
                                "a_x",
                                "a_y",
                                "a_z"
                            ],
                            "correction_factors": [
                                1,
                                1,
                                1
                            ],
                            "low_frequency_ignore": {
                                "frequency": 0.01,
                                "threshold": 100000
                            }
                        }
                    ]
                }
            }
        },
        {
            label: 'SGRE Forecast',
            data: {
                extra: {
                    "type": "sgre-forecast",
                    "credentials": {
                        "ClientId": "xxx",
                        "Instance": "xxx",
                        "TenantId": "xxx",
                        "WeatherAPI": "xxx",
                        "ClientSecret": "xxx",
                        "WeatherScope": "xxx",
                        "ClimatologyAPI": "xxx",
                        "SecretExpiration": "xxx"
                    },
                    "emplacementId": "xxx"
                }
            }
        },
        {
            label: 'Maching Learning',
            data: {
                extra: {
                    "type": "machine_learning",
                    "model": "sgre/model1.onnx",
                    "input_source": "East Anglia 3 Forecasts",
                    "input_channels": ["swh", "pwp"]
                }
            }
        },
        {
            label: 'DMI Forecast',
            data: {
                extra: {
                    "gps": {
                        "lat": 55.681138,
                        "lon": 12.555914
                    },
                    "type": "dmi_forecast",
                    "channels": [
                        {
                            "name": "wind-speed-10m",
                            "collection": "harmonie_dini_sf"
                        },
                        {
                            "name": "wind-dir-10m",
                            "collection": "harmonie_dini_sf"
                        }
                    ]
                }
            }
        }, {
            label: 'DMI Observation',
            data: {
                extra: {
                    type: "dmi_observation",
                    dataset: "metObs",
                    channels: [
                        {
                            name: "wind_speed",
                            scale: 1,
                            valueType: "velocity",
                            parameterId: "wind_speed"
                        },
                        {
                            name: "wind_dir",
                            scale: 1,
                            valueType: "direction",
                            parameterId: "wind_dir"
                        }
                    ],
                    stationId: "06180"
                }
            }
        }, {
            label: 'Watchdog',
            data: {
                extra: {
                    type: "watchdog",
                    users: [
                        "sme@sohansen.dk"
                    ],
                    inputs: {
                        winddir: {
                            source: {
                                id: 34,
                                run: "latest",
                                type: "forecast",
                                forecast_type: "wind-dir-10m"
                            },
                            aggregation: "mean"
                        },
                        windspeed: {
                            source: {
                                id: 34,
                                run: "latest",
                                type: "forecast",
                                forecast_type: "wind-speed-10m"
                            },
                            aggregation: "max"
                        }
                    },
                    interval: 3600,
                    expression: "windspeed > 1.0 and 10 < winddir < 290",
                    evaluationWindow: {
                        past: 3600,
                        future: 259200
                    },
                    aggregationWindow: 3600
                }
            }
        }, {
            label: 'Open-Meteo Forecast',
            data: {
                extra: {
                    "url": "https://api.open-meteo.com/v1/forecast?latitude=55.681138&longitude=12.555914&hourly=wind_speed_10m,wind_direction_10m&wind_speed_unit=ms&timezone=UTC",
                    "type": "open_meteo",
                    "channels": [
                        "wind_speed_10m",
                        "wind_direction_10m"
                    ]
                }
            }
        }
    ];
    
    const handleTemplateSelect = (event) => {
        const template = sourceTemplates[event.target.value];
        if (!template) return;
        setCurrentSource((prev) => ({
            ...prev,
            ...template.data,
        }));
        setExtraJSON(JSON.stringify(template.data.extra, null, 2));
    };
    
    return (
        <div>
            {user.hasAnyRole(['jedimaster', 'admin']) && (
                <Button variant="contained" color="primary" onClick={() => handleOpen(null)}>
                    Add Source
                </Button>
            )}
            {error && typeof error === 'string' && <p>{error}</p>}
            <List>
                {Array.isArray(sources) && sources.map(source => (
                    <ListItem key={source.id}>
                        {user.hasAnyRole(['jedimaster', 'admin']) && (
                            <>
                                <IconButton edge="end" aria-label="edit" onClick={() => handleOpen(source)} sx={{ mr: 1 }} >
                                    <EditIcon />
                                </IconButton>
                            </>
                        )}
                        <ListItemText primary={source.name} secondary={JSON.stringify(source.extra)} />
                        {user.hasAnyRole(['jedimaster', 'admin']) && (
                            <>
                                <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(source.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        )}
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{isEditMode ? 'Edit Source' : 'Add New Source'}</DialogTitle>
                <Select
                    value=""
                    onChange={handleTemplateSelect}
                    displayEmpty
                    fullWidth
                    style={{ marginBottom: '16px' }}
                >
                    <MenuItem value="" disabled>Select a Template</MenuItem>
                    {sourceTemplates.map((template, idx) => (
                        <MenuItem key={idx} value={idx}>{template.label}</MenuItem>
                    ))}
                </Select>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Name"
                        type="text"
                        fullWidth
                        name="name"
                        value={currentSource.name}
                        onChange={handleInputChange}
                    />
                    <Select
                        label="Site"
                        value={currentSource.site_id}
                        onChange={handleInputChange}
                        fullWidth
                        displayEmpty
                        name="site_id"
                    >
                        <MenuItem value="" disabled>Select a Site</MenuItem>
                        {sites.map((site) => (
                            <MenuItem key={site.id} value={site.id}>
                                {site.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <TextareaAutosize
                        aria-label="extra"
                        minRows={5}
                        placeholder="Extra JSON"
                        name="extra"
                        value={extraJSON}
                        onChange={handleExtraChange}
                        style={{ width: '100%', marginTop: '16px', padding: '8px' }}
                    />
                    {error && <p>{error}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleAddOrUpdateSource}>{isEditMode ? 'Update Source' : 'Add Source'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SourceManagerComponent;
